import { useEffect, useState, useMemo, useRef } from "react";
import { useAuth } from "react-oidc-context";
import { CircularProgress } from "@mui/material";
import AppBar from "./components/AppBar";
import Map from "./components/Map";
import InsightsPanel from "../../panels/insightPanel";
import AddLocationPanel from "../../panels/addLocationPanel";
import NitroMapButtons from "../../components/mapButtons";
import AddFOBIndicationPanel from "../../panels/addFOBIndicationPanel";
import QuotesPanel from "../../panels/quotesPanel";
import Notifications from "./components/Notifications";
import NitroStateManagement from "../../store/state";
import "./index.css";
import GuidancePanel from "../../panels/guidancePanel";
import SettingsPanel from "../../panels/settings";
import { useIsMobile } from "../../hooks/useIsMobile";
import { useNitroStore } from "../../store/Store";
import DesktopOriginManagerPanel from "./favoriteOrigin/DesktopOriginManagerPanel";
import Filters from "./components/filters";
import IndicationHistoryPanel from "./favoriteOrigin/IndicationHistoryPanel";
import AddIndicationPanel from "./favoriteOrigin/AddIndicationPanel";
import FreightDetailPanelQMD from "../../panels/multiLocationQuotePanel/components/RailFreightDetailPanel";
import { useNitroCAStore } from "../../store/CompetitiveAnalysisStore";
import { useQuotesForDestinations } from "../../hooks/useQuotesForDestinations";
import { useFavoriteOrigins } from "../../hooks/useFavoriteOrigins";
import { IGetQuotesParams } from "../../types/IQuotes";
import { convertQuotesToRoutes } from "../../utils/convertQuotesToRoutes";
import { DestinationOriginPanelMobile } from "../../panels/multiLocationQuotePanel/destinationOriginPanelMobile";
import TroubleshootQuote from "../../panels/multiLocationQuotePanel/components/TroubleshootQuote";
import MobileSubFooter from "../../panels/mobile/multiLocQuotePanel/components/mobileSubFooter";
import FooterMultiLocation from "../../panels/mobile/multiLocQuotePanel/components/footerMultiLocation";
import MobileModeToggleGroup from "../../panels/mobile/multiLocQuotePanel/components/mobileModeToggleGroup";
import TruckFreightDetailPanel from "../../panels/multiLocationQuotePanel/components/TruckFreightDetailPanel";
import AdditionalCarriersPanel from "../../panels/multiLocationQuotePanel/components/AdditionalCarriersPanel";
import InboundModeTogglePanel from "../../panels/multiLocationQuotePanel/components/InboundModeTogglePanel";
import AddProductToDestinationPanel from "../../panels/multiLocationQuotePanel/components/AddProductToDestinationPanel";
import AddProductToOriginPanel from "../../panels/multiLocationQuotePanel/components/AddProductToOriginPanel";
import SelectRailStationPanel from "../../panels/multiLocationQuotePanel/components/SelectRailStationPanel";
import TurnOffModePanel from "../../panels/multiLocationQuotePanel/components/TurnOffModePanel";
import MapLegend from "../../components/mapButtons/MapLegend";
import LabelTypeButtons from "../../components/mapButtons/LabelTypeButtons";
import RailRateRequestPanel from "../../panels/multiLocationQuotePanel/components/RailRateRequestPanel";
import RailRateRequestFailedPopup from "../../panels/multiLocationQuotePanel/components/RailRateRequestFailedPopup";
import { TariffPollRunner } from "../../panels/multiLocationQuotePanel/components/TariffPollRunner";
import FactoryIcon from "../../svg/factory.svg?react";
import { icons } from "../../svg";
import { QuoteMultipleDestinationsPanel } from "../../panels/multiLocationQuotePanel/QuoteMultipleDestinationsPanel";

const HomePage = () => {
  const isMobile = useIsMobile(768);
  const auth = useAuth();
  const user = `${auth.user?.profile.given_name} ${auth.user?.profile.family_name}`;
  const [
    showGuidancePanel,
    setShowGuidancePanel,
    product,
    setProduct,
    priorityProducts,
    refetchFavoriteOrigins,
    triggerRefetchFavoriteOrigins,
    favoriteOrigins,
    setFavoriteOrigins,
    setFavoriteOriginsLoading,
    currentFilters,
    activeFilter,
    uom,
    currency,
    setMultiLocationDestinations,
    showMobileFavoriteOriginPanel,
    setShowMobileFavoriteOriginPanel,
    showMobileSearchPanel,
    setShowSettingsPanel,
    setShowMobileSearchPanel,
    showSettingsPanel,
    setFilter,
    showAddLocationPanel,
    selectingPinLocation,
    isFilterLoading,
    originManagerCloseRequestNonce,
    originManagerIsSyncing,
    originManagerHasUnsavedPriceEdits,
    triggerOriginManagerUnsavedNavAttempt,
  ] = useNitroStore((state) => [
    state.showGuidancePanel,
    state.setShowGuidancePanel,
    state.product,
    state.setProduct,
    state.priorityProducts,
    state.refetchFavoriteOrigins,
    state.triggerRefetchFavoriteOrigins,
    state.favoriteOrigins,
    state.setFavoriteOrigins,
    state.setFavoriteOriginsLoading,
    state.currentFilters,
    state.activeFilter,
    state.uom,
    state.currency,
    state.setMultiLocationDestinations,
    state.showMobileFavoriteOriginPanel,
    state.setShowMobileFavoriteOriginPanel,
    state.showMobileSearchPanel,
    state.setShowSettingsPanel,
    state.setShowMobileSearchPanel,
    state.showSettingsPanel,
    state.setFilter,
    state.showAddLocationPanel,
    state.selectingPinLocation,
    state.isLoading,
    state.originManagerCloseRequestNonce,
    state.originManagerIsSyncing,
    state.originManagerHasUnsavedPriceEdits,
    state.triggerOriginManagerUnsavedNavAttempt,
  ]);
  const [
    displayRailFreightDetailsPanel,
    displayTruckFreightDetailsPanel,
    displayAdditionalCarriersPanel,
    displayInboundModeTogglePanel,
    selectedTruckDetailRecord,
    troubleshootQuoteOpen,
    displayAddProductToDestinationPanel,
    displayAddProductToOriginPanel,
    additionalCarriers,
    inboundModes,
    displayAddInboundRailPanel,
    displayTurnOffModePanel,
    displayRailRateRequestPanel,
    showRailRateRequestFailedPopup,
  ] = useNitroCAStore((state) => [
    state.displayRailFreightDetailsPanel,
    state.displayTruckFreightDetailsPanel,
    state.displayAdditionalCarriersPanel,
    state.displayInboundModeTogglePanel,
    state.selectedTruckDetailRecord,
    state.troubleshootQuoteOpen,
    state.displayAddProductToDestinationPanel,
    state.displayAddProductToOriginPanel,
    state.additionalCarriers,
    state.inboundModes,
    state.displayAddInboundRailPanel,
    state.displayTurnOffModePanel,
    state.displayRailRateRequestPanel,
    state.showRailRateRequestFailedPopup,
  ]);
  const [refetchQuotes, triggerRefetchQuotes] = useNitroCAStore((state) => [
    state.refetchQuotes,
    state.triggerRefetchQuotes,
  ]);

  const shouldShowMobileModeToggle =
    isMobile &&
    !showMobileFavoriteOriginPanel &&
    !showSettingsPanel &&
    !showMobileSearchPanel;

  const [favoriteOriginsLoaded, setFavoriteOriginsLoaded] = useState(false);

  // TODO: when a user removes product from priority list nothing gets default highlighted
  const findDefaultProduct = () => {
    // if we haven't loaded the favorite origins yet, do not default a product
    if (!favoriteOriginsLoaded) {
      return "";
    }
    // once favorite origins have loaded: if user has no favorite origins use their priority list to set product. Ammonia is prioritized over other products.
    if (favoriteOrigins.length === 0) {
      const initialProduct = priorityProducts.includes("AMMONIA")
        ? "AMMONIA"
        : priorityProducts[0];
      return initialProduct;
    }
    // if user has favorite origins, default to ammonia if they have it, otherwise default to the first product in their favorite origins list
    const favoriteOriginProducts = favoriteOrigins.map((o) =>
      o.product.toUpperCase(),
    );
    return favoriteOriginProducts.includes("AMMONIA")
      ? "AMMONIA"
      : favoriteOriginProducts[0];
  };
  const defaultProduct = findDefaultProduct();

  const splitMonthKeys = useMemo(() => {
    const keys = new Set<string>();
    const originsForProduct =
      favoriteOrigins.find((origin) => origin.product.toUpperCase() === product)
        ?.favoriteLocations || [];

    originsForProduct.forEach((favorite) => {
      favorite.months.forEach((favoriteMonth) => {
        if (!favoriteMonth.split) return;
        keys.add(favoriteMonth.month);
      });
    });
    return Array.from(keys);
  }, [favoriteOrigins, product]);

  // Fetch and manage favorite origins for the current user using the custom hook.
  const favoriteParams = useMemo(() => ({ marketer: user }), [user]);
  const { favorites, loading: favoriteOriginsLoading } = useFavoriteOrigins(
    favoriteParams,
    refetchFavoriteOrigins,
  );
  useEffect(() => {
    setFavoriteOrigins(favorites);
  }, [favorites, setFavoriteOrigins]);
  useEffect(() => {
    setFavoriteOriginsLoading(favoriteOriginsLoading);
  }, [favoriteOriginsLoading, setFavoriteOriginsLoading]);
  useEffect(() => {
    if (!favoriteOriginsLoading) {
      setFavoriteOriginsLoaded(true);
    }
  }, [favoriteOriginsLoading]);

  const productResponse = favoriteOrigins.filter(
    (o) => o.product === (product || "AMMONIA"),
  )[0];
  const originsByProduct = productResponse?.favoriteLocations || [];
  const kochOrigins = originsByProduct.filter((f) => !f.competitor);
  const competitorOrigins = originsByProduct.filter((f) => f.competitor);

  // either koch or competitor origin must be selected to activate the rest of the app
  const searchDisabled =
    kochOrigins.length === 0 && competitorOrigins.length === 0;
  const showSideBar =
    currentFilters.city || currentFilters.location || currentFilters.party;
  const quoteParams: IGetQuotesParams = {
    id: currentFilters.city || currentFilters.location,
    product: product || "UAN",
    party: currentFilters.party || null,
    marketer: user,
    shipDate: currentFilters.month || "",
    currency: currency || "USD",
    uom: uom || "ST",
  };

  // Only fetch on desktop - mobile uses the hook in MobileFilter component.
  const quotesResult = useQuotesForDestinations(
    quoteParams,
    refetchQuotes,
    isMobile,
  );

  const { quotes, loading, error } = quotesResult;

  const truckDlvdOption = !quotes.length
    ? "px"
    : quotes[0].destination.truckDlvdOption;
  const railDlvdOption = !quotes.length
    ? "px"
    : quotes[0].destination.railDlvdOption;

  useEffect(() => {
    if (quotes.length > 0) {
      const routes = convertQuotesToRoutes(quotes);
      setMultiLocationDestinations(routes);
    }
  }, [quotes, setMultiLocationDestinations]);

  const [footerTab, setFooterTab] = useState<any>("origins");
  const [lastOriginsView, setLastOriginsView] = useState<"map" | "list">("map");
  const [isOriginsButtonActive, setIsOriginsButtonActive] = useState(false);
  const [isClosingOriginManager, setIsClosingOriginManager] = useState(false);
  const [isAwaitingOriginManagerSync, setIsAwaitingOriginManagerSync] =
    useState(false);
  const [isOriginManagerAnimatedOpen, setIsOriginManagerAnimatedOpen] =
    useState(false);
  const [keepOriginManagerMounted, setKeepOriginManagerMounted] =
    useState(false);
  const [
    reopenOriginManagerAfterAddLocation,
    setReopenOriginManagerAfterAddLocation,
  ] = useState(false);
  const wasAddLocationPanelOpenRef = useRef(false);
  const handledOriginManagerCloseRequestNonceRef = useRef(0);

  useEffect(() => {
    if (isOriginsButtonActive && !isClosingOriginManager) {
      const raf = requestAnimationFrame(() =>
        setIsOriginManagerAnimatedOpen(true),
      );
      return () => cancelAnimationFrame(raf);
    }
    setIsOriginManagerAnimatedOpen(false);
    return undefined;
  }, [isOriginsButtonActive, isClosingOriginManager]);

  const shouldRenderQuoteSidebar = showSideBar && !isMobile;

  const handleOpenOriginManager = () => {
    if (isOriginsButtonActive) return;

    setIsClosingOriginManager(false);
    setIsOriginsButtonActive(true);

    // Refetch on open in case a shared Koch origin changed while this panel was closed.
    if (user) {
      triggerRefetchFavoriteOrigins();
    }
  };

  const handleCloseOriginManager = () => {
    if (!isOriginsButtonActive) return;

    if (originManagerHasUnsavedPriceEdits) {
      triggerOriginManagerUnsavedNavAttempt("close");
      return;
    }

    if (originManagerIsSyncing) {
      setIsAwaitingOriginManagerSync(true);
      return;
    }

    triggerRefetchQuotes();
    setIsClosingOriginManager(true);
  };

  useEffect(() => {
    if (!isAwaitingOriginManagerSync || originManagerIsSyncing) {
      return;
    }

    setIsAwaitingOriginManagerSync(false);
    triggerRefetchQuotes();
    setIsClosingOriginManager(true);
  }, [
    isAwaitingOriginManagerSync,
    originManagerIsSyncing,
    triggerRefetchQuotes,
  ]);

  const handleOriginManagerTransitionEnd = () => {
    if (!isClosingOriginManager) return;
    setIsOriginsButtonActive(false);
    setIsClosingOriginManager(false);
  };

  useEffect(() => {
    if (!isClosingOriginManager) {
      return;
    }

    // Fallback in case transitionend is skipped/interrupted; prevents panel from
    // getting stuck in a closing state and blocking reopen from sidebar button.
    const fallback = setTimeout(() => {
      setIsOriginsButtonActive(false);
      setIsClosingOriginManager(false);
    }, 320);

    return () => clearTimeout(fallback);
  }, [isClosingOriginManager]);

  useEffect(() => {
    if (!originManagerCloseRequestNonce) {
      return;
    }

    if (
      handledOriginManagerCloseRequestNonceRef.current ===
      originManagerCloseRequestNonce
    ) {
      return;
    }

    handledOriginManagerCloseRequestNonceRef.current =
      originManagerCloseRequestNonce;

    if (isOriginsButtonActive && !isClosingOriginManager) {
      if (originManagerIsSyncing) {
        setIsAwaitingOriginManagerSync(true);
      } else {
        triggerRefetchQuotes();
        setIsClosingOriginManager(true);
      }
    }
  }, [
    originManagerCloseRequestNonce,
    isOriginsButtonActive,
    isClosingOriginManager,
    originManagerIsSyncing,
    triggerRefetchQuotes,
  ]);

  const isOriginManagerOpen = isOriginsButtonActive && !isClosingOriginManager;
  const isFilterBackdropActive =
    !isOriginManagerOpen &&
    (activeFilter === "company" ||
      activeFilter === "location" ||
      activeFilter === "month");
  const shouldDimOriginsSidebar = isFilterLoading || isFilterBackdropActive;

  // offset for the insights panel
  const insightsPanelLeftOffset =
    20 +
    (!isMobile ? 45 : 0) +
    (shouldRenderQuoteSidebar || isOriginManagerOpen ? 800 : 0);

  useEffect(() => {
    const wasOpen = wasAddLocationPanelOpenRef.current;
    const isOpen = showAddLocationPanel;

    if (!wasOpen && isOpen && isOriginManagerOpen) {
      // Add Location was opened while OM is visible: close OM, but keep it mounted
      // so panel state is preserved and can be restored after Add Location closes.
      setKeepOriginManagerMounted(true);
      setReopenOriginManagerAfterAddLocation(true);
      setIsClosingOriginManager(true);
    }

    if (wasOpen && !isOpen && reopenOriginManagerAfterAddLocation) {
      setIsClosingOriginManager(false);
      setIsOriginsButtonActive(true);
      setReopenOriginManagerAfterAddLocation(false);
      setKeepOriginManagerMounted(false);
    }

    wasAddLocationPanelOpenRef.current = isOpen;
  }, [
    showAddLocationPanel,
    isOriginManagerOpen,
    reopenOriginManagerAfterAddLocation,
  ]);

  useEffect(() => {
    if (isMobile) {
      return;
    }

    if (!isOriginsButtonActive || isClosingOriginManager) {
      return;
    }

    if (
      activeFilter === "company" ||
      activeFilter === "location" ||
      activeFilter === "month"
    ) {
      setIsClosingOriginManager(true);
    }
  }, [isMobile, isOriginsButtonActive, isClosingOriginManager, activeFilter]);

  useEffect(() => {
    if (isMobile) {
      return;
    }

    if (!isOriginsButtonActive || isClosingOriginManager) {
      return;
    }

    if (selectingPinLocation) {
      setIsClosingOriginManager(true);
    }
  }, [
    isMobile,
    isOriginsButtonActive,
    isClosingOriginManager,
    selectingPinLocation,
  ]);

  const getOriginManagerClass = () => {
    if (isClosingOriginManager) return "closing-right";
    if (isOriginManagerAnimatedOpen) return "open";
    return "";
  };

  const handleFooterSelect = (tab: any) => {
    setFooterTab(tab);
    if (tab === "quote") {
      triggerRefetchQuotes();
    }

    if (tab === "account") {
      if (isMobile) {
        setLastOriginsView(showMobileFavoriteOriginPanel ? "list" : "map");
      }
      setShowSettingsPanel(true);
      setShowMobileFavoriteOriginPanel(false);
      setShowMobileSearchPanel(false);
    } else if (tab === "origins") {
      if (isMobile) {
        setShowMobileFavoriteOriginPanel(lastOriginsView === "list");
      } else {
        setShowMobileFavoriteOriginPanel(true);
      }
      setShowSettingsPanel(false);
      setShowMobileSearchPanel(false);
    } else if (tab === "quote") {
      if (isMobile) {
        setLastOriginsView(showMobileFavoriteOriginPanel ? "list" : "map");
      }
      setShowMobileSearchPanel(true);
      setShowSettingsPanel(false);
      setShowMobileFavoriteOriginPanel(false);
    }
  };
  return (
    <>
      <div className="home-page-container">
        <div
          className={`layout-wrapper${
            isOriginManagerOpen && !isMobile ? " origin-manager-open" : ""
          }`}
        >
          {!selectingPinLocation && (
            <div className="header-a">
              <AppBar
                searchDisabled={searchDisabled}
                defaultProduct={defaultProduct}
                disableProductFilter={loading}
                onFooterSelect={handleFooterSelect}
              />
            </div>
          )}

          <div
            id="origin-manager-container"
            className={`body-container ${
              shouldRenderQuoteSidebar ? "with-sidebar" : "full-width"
            } ${!isMobile && !selectingPinLocation ? "with-origin-rail" : ""} ${
              isFilterBackdropActive ? "filter-active" : ""
            } ${isOriginManagerOpen && !isMobile ? "origin-manager-open" : ""}`}
          >
            {/* Desktop sidebar navigation panel */}
            {!isMobile && !selectingPinLocation && (
              <aside
                className={`home-vertical-sidebar${
                  searchDisabled ? " disabled" : ""
                }${shouldDimOriginsSidebar ? " dimmed" : ""}`}
                aria-label="Origins quick action"
              >
                <button
                  id="home-vertical-sidebar-origins-button"
                  type="button"
                  className={`home-vertical-sidebar-button ${
                    isOriginsButtonActive ? "active" : ""
                  }`}
                  aria-label="Origins"
                  onClick={
                    isOriginsButtonActive
                      ? handleCloseOriginManager
                      : handleOpenOriginManager
                  }
                >
                  <span className="home-vertical-sidebar-icon">
                    <FactoryIcon width={24} height={24} fill="currentColor" />
                  </span>
                  <span className="home-vertical-sidebar-label">Origins</span>
                </button>
              </aside>
            )}

            {/* Panels that overlay on top of map. These are not with the other panel section down below since these need to be positioned differently */}
            {displayAdditionalCarriersPanel && (
              <AdditionalCarriersPanel {...{ carriers: additionalCarriers }} />
            )}
            {displayInboundModeTogglePanel && inboundModes && (
              <InboundModeTogglePanel {...{ inboundModes }} />
            )}
            {/* Always-on polling runner — survives modal open/close cycles */}
            <TariffPollRunner />

            {/* Desktop and Mobile Quotes Panels */}
            {!showAddLocationPanel &&
              shouldRenderQuoteSidebar &&
              !selectingPinLocation && (
                <div
                  className="sidebar-b quote-screen-sidebar"
                  style={{ width: "800px" }}
                >
                  {loading && (
                    <div className="loading-spinner-container">
                      <CircularProgress />
                    </div>
                  )}
                  {!loading && error && (
                    <div style={{ padding: "20px", color: "red" }}>
                      Error loading data: {error.message}
                    </div>
                  )}
                  {!loading &&
                    !error &&
                    (isMobile ? (
                      <DestinationOriginPanelMobile
                        destinationQuotes={quotes}
                        party={currentFilters.party}
                      />
                    ) : (
                      <>
                        {/* New QMD panel */}
                        {!showAddLocationPanel && (
                          <QuoteMultipleDestinationsPanel
                            destinationQuotes={quotes}
                            favoriteOrigins={originsByProduct}
                            handleOpenOriginManager={handleOpenOriginManager}
                            initialTruckDlvdOption={truckDlvdOption}
                            initialRailDlvdOption={railDlvdOption}
                          />
                        )}
                        {displayTruckFreightDetailsPanel &&
                          selectedTruckDetailRecord && (
                            <TruckFreightDetailPanel
                              {...selectedTruckDetailRecord}
                            />
                          )}
                      </>
                    ))}
                </div>
              )}

            {/* Desktop Origin Manager Panel */}
            {!isMobile &&
              (isOriginsButtonActive ||
                isClosingOriginManager ||
                keepOriginManagerMounted) && (
                <div
                  className={`origin-manager-slide-wrapper ${getOriginManagerClass()}`}
                  style={
                    !isOriginsButtonActive && keepOriginManagerMounted
                      ? { display: "none" }
                      : undefined
                  }
                  onTransitionEnd={handleOriginManagerTransitionEnd}
                >
                  {/* Rendered before the panel and stacked behind it so the panel edge covers part of the button, leaving a tab sticking out */}
                  <button
                    type="button"
                    className="origin-manager-close-btn"
                    onClick={handleCloseOriginManager}
                    aria-label={
                      shouldRenderQuoteSidebar ? "Back" : "Close Origin Manager"
                    }
                  >
                    <img
                      data-tooltip-id="origin-close-button"
                      src={icons.OriginCloseButtonIcon}
                      alt="origin-close-button"
                      className="origin-close-button"
                    />
                  </button>
                  <div className="origin-manager-overlay">
                    {favoriteOriginsLoading && (
                      <div className="origin-manager-fetch-loading-overlay">
                        <CircularProgress />
                      </div>
                    )}
                    <div className="origin-manager-overlay-body">
                      <DesktopOriginManagerPanel
                        selectedProduct={product || "UAN"}
                        priorityProducts={priorityProducts}
                        kochOrigins={kochOrigins}
                        competitorOrigins={competitorOrigins}
                        isQuoteScreenOpen={!!shouldRenderQuoteSidebar}
                        onSaveComplete={() => {
                          triggerRefetchFavoriteOrigins();
                          triggerRefetchQuotes();
                        }}
                        onProductSelect={(selectedProduct) => {
                          setProduct(selectedProduct);
                          triggerRefetchQuotes();
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}

            {/* Content on the right side of the screen */}
            <div className="main-content-c">
              {/* Map and map controls */}
              <div className="map-area-wrapper">
                <Map searchDisabled={searchDisabled} />

                {/* Desktop map buttons */}
                {!isMobile && (
                  <>
                    {showSideBar && (
                      <div className="mapControlsLeft">
                        <LabelTypeButtons />
                      </div>
                    )}
                    <div className="mapControlsStack">
                      {showSideBar && <MapLegend />}
                      <NitroMapButtons />
                    </div>
                  </>
                )}

                <NitroStateManagement />
                <Notifications />
              </div>

              {/* Helper panels */}
              <div className="popup-overlay-d">
                <div className="popup-panel">
                  <AddLocationPanel />
                  <AddFOBIndicationPanel />
                  {displayRailFreightDetailsPanel && <FreightDetailPanelQMD />}
                  <QuotesPanel />
                  <GuidancePanel
                    initialProduct={product}
                    open={showGuidancePanel}
                    close={() => setShowGuidancePanel(false)}
                  />
                  <IndicationHistoryPanel />
                  <AddIndicationPanel />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Rendered outside body-container/header-a: fixed-position panels are still capped by an ancestor's stacking context, so these must sit above both to outrank the AppBar */}
      {displayAddInboundRailPanel && (
        <>
          <div className="fullscreen-background-overlay" />
          <SelectRailStationPanel />
        </>
      )}
      {displayTurnOffModePanel && (
        <>
          <div className="fullscreen-background-overlay" />
          <TurnOffModePanel />
        </>
      )}
      {displayRailRateRequestPanel && (
        <>
          <div className="fullscreen-background-overlay" />
          <RailRateRequestPanel />
        </>
      )}
      {showRailRateRequestFailedPopup && <RailRateRequestFailedPopup />}
      {troubleshootQuoteOpen && (
        <div className="fullscreen-background-overlay" />
      )}
      <div className="troubleshoot-quote-panel">
        <div style={{ pointerEvents: "auto" }}>
          <TroubleshootQuote />
        </div>
      </div>
      {displayAddProductToDestinationPanel && (
        <>
          <div className="fullscreen-background-overlay" />
          <AddProductToDestinationPanel />
        </>
      )}
      {displayAddProductToOriginPanel && (
        <>
          <div className="fullscreen-background-overlay" />
          <AddProductToOriginPanel />
        </>
      )}
      <div
        style={
          {
            display: "contents",
            "--insights-panel-left": `${insightsPanelLeftOffset}px`,
          } as React.CSSProperties
        }
      >
        <InsightsPanel />
      </div>
      {/* Mobile mode toggle - top center */}
      {shouldShowMobileModeToggle && (
        <div
          style={{
            position: "fixed",
            top: "30px",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 1000,
            pointerEvents: "auto",
          }}
        >
          <MobileModeToggleGroup />
        </div>
      )}
      {/* Mobile footer - rendered outside containers to float above all panels */}
      {isMobile && (
        <div
          className="mobile-bottom-stack"
          style={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: showSettingsPanel ? 10004 : 10002,
          }}
        >
          {!showMobileFavoriteOriginPanel &&
            !showMobileSearchPanel &&
            !showSettingsPanel && (
              <MobileSubFooter
                onMapClick={() => handleFooterSelect("origins")}
                selectedMonth={currentFilters.month}
                setSelectedMonth={(value) => setFilter("month", value)}
                splitMonthKeys={splitMonthKeys}
              />
            )}

          {!showMobileSearchPanel && !showMobileFavoriteOriginPanel && (
            <div style={{ width: "100%" }}>
              <FooterMultiLocation
                selected={footerTab}
                onSelect={handleFooterSelect}
              />
            </div>
          )}
        </div>
      )}
      {/* Mobile filters */}
      {isMobile && (
        <Filters
          searchDisabled={searchDisabled}
          defaultProduct={defaultProduct}
          disableProductFilter={loading}
          onFooterSelect={handleFooterSelect}
        />
      )}{" "}
      <SettingsPanel onFooterSelect={handleFooterSelect} />
    </>
  );
};

export default HomePage;
